"use client";

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from "react";
import { useRouter } from "next/navigation";
import CommunityChat from "../CommunityChat";
import NotificationSettings from "../NotificationSettings";
import {
  ADMIN_TG,
  FS_PREF_KEY,
  ensureTelegramReady,
  useTelegramFullscreen,
} from "../useCommunityChat";
import { getDeviceSignals } from "../device-fp";
import { parseStartParam, readStartParam } from "./deeplink";
import {
  IconBell,
  IconChat,
  IconCollapse,
  IconExpand,
  IconWallpaper,
} from "./TgIcons";
import ChatBackgroundPicker from "../ChatBackgroundPicker";
import ChatBackground from "./ChatBackground";
import MiddleHeader from "./MiddleHeader";
import MessageBubble from "./MessageBubble";
import VouchHistory from "./VouchTopic";
import {
  ANNOUNCEMENT_SEED_BODY,
  ANNOUNCEMENT_SEED_PHOTO,
  ANNOUNCEMENT_SEED_TEXT,
  ANNOUNCEMENT_SEED_TIME,
  CHAT_NOTICE_SEED_TEXT,
  README_SEED_BODY,
  README_SEED_PHOTO,
  README_SEED_REACTIONS,
  README_SEED_TEXT,
  README_SEED_TIME,
  SEED_AUTHOR,
} from "./seed";
import type { TopicPreview } from "@/lib/community";
import type { TopicDef, TopicKey, VouchView } from "./types";
import {
  CustomEmojiImg,
  TIMELINE_SEED_ORDER,
  renderBody,
  renderTextWithEmoji,
  shortDateLabel,
  tokenPreview,
} from "./format";

/**
 * The full-viewport Telegram Web A replica for /community, emitting the real
 * client's DOM: .tg-html/.tg-body stand-ins carrying the saved runtime style
 * vars, #Main with the saved column classes, #LeftColumn (topic-list panel
 * with #TopicListHeader and the 65px virtualised .chat-list rows) and
 * #MiddleColumn (.messages-layout hosting the active topic). Vouch topics
 * (Testimonials, BUY4U, Announcements) are read-only mirrors of the group;
 * READ ME shows the welcome post; Group Chat is the live chat.
 */

/**
 * Topics in the saved topic list's order, with each icon's real
 * custom-emoji document id. Group Chat uses the official Telegram "Topics"
 * forum-icon pack's animated 💬 bubble (owner swapped it in for the old #
 * glyph); BUY4U keeps the Duck-pack ✈️ but ANIMATED — its static still is a
 * plain plane, the duck riding it only exists in the animation.
 */
const TOPICS: TopicDef[] = [
  {
    key: "chat",
    title: "Group Chat",
    emoji: "💬",
    docId: "5417915203100613993",
    animated: true,
    peer: 4,
  },
  {
    key: "readme",
    title: "READ ME",
    emoji: "‼️",
    docId: "5440660757194744323",
    peer: 0,
  },
  {
    key: "announcements",
    title: "Announcements",
    emoji: "📣",
    docId: "5424818078833715060",
    peer: 1,
  },
  {
    key: "buy4u",
    title: "BUY4U Vouches",
    emoji: "✈️",
    docId: "5231361378748472914",
    animated: true,
    peer: 5,
  },
  {
    key: "testimonials",
    title: "Client Testimonials",
    emoji: "⭐️",
    docId: "5438496463044752972",
    peer: 3,
  },
];

/** Web A topic icon: custom-emoji artwork, or the # icon for Group Chat. */
function TopicIcon({ def }: { def: TopicDef }) {
  if (!def.docId) {
    return (
      <i
        className="icon icon-hashtag I0-98vJl P6JY5GgC general-forum-icon"
        aria-hidden
      />
    );
  }
  return (
    <div
      className="zOQgNBAa P6JY5GgC custom-emoji emoji tg-topic-icon"
      data-alt={def.emoji}
      aria-hidden
    >
      <CustomEmojiImg
        id={def.docId}
        alt={def.emoji}
        preferAnimated={def.animated}
      />
    </div>
  );
}

const ROW_HEIGHT = 65;
const MAIN_STYLE = { "--pattern-color": "#4A8E3A8C" } as CSSProperties;
/** Number of topic-list aura palettes (see `.tg-topic-aura-*` in telegram.css). */
const AURA_COUNT = 10;

export interface ChatPreview {
  authorName: string;
  body: string;
  createdAt: string;
}

interface RowMeta {
  sender: string | null;
  summary: string;
  time: string;
  /** True when the row shows an unsent composer draft (red "Draft:" prefix). */
  draft?: boolean;
}

function latest(vouches: VouchView[]): VouchView | undefined {
  let best: VouchView | undefined;
  for (const v of vouches) {
    if (
      !best ||
      (v.originDate ?? v.createdAt) > (best.originDate ?? best.createdAt)
    )
      best = v;
  }
  return best;
}

export default function TelegramApp({
  testimonials,
  buy4u,
  announcements,
  welcome,
  seedReadme = "",
  seedAnnouncement = "",
  seedChatNotice = "",
  seedPins = {},
  seedHidden = {},
  memberLabel,
  chatPreview,
  topicPreviews = {},
}: {
  testimonials: VouchView[];
  buy4u: VouchView[];
  announcements: VouchView[];
  welcome: string;
  /** Persisted admin override for the READ ME seed body ("" = use the built-in). */
  seedReadme?: string;
  /** Persisted admin override for the announcement seed body ("" = built-in). */
  seedAnnouncement?: string;
  /** Persisted admin override for the chat-notice seed body ("" = built-in). */
  seedChatNotice?: string;
  /** Persisted admin pin state per seed slot (readme/welcome/announcement/chat-notice). */
  seedPins?: Record<string, boolean>;
  /** Persisted admin delete (hide) state per seed slot. */
  seedHidden?: Record<string, boolean>;
  memberLabel: string;
  chatPreview: ChatPreview | null;
  /** Newest LIVE post per topic — read-only rows merge it with their vouches. */
  topicPreviews?: Record<string, TopicPreview>;
}) {
  const router = useRouter();
  const [active, setActive] = useState<TopicKey | null>(null);
  const [inTg, setInTg] = useState(false);
  // Ban gate: a banned member must not see the community when opening the
  // Mini App — not even the topic list. "checking" (transient) renders a blank
  // themed screen; "blocked" (permanent) shows the Rose-style banned notice
  // (+ reason, when one was given with /ban). Web/non-banned = "ok".
  const [gate, setGate] = useState<"ok" | "checking" | "blocked">("ok");
  // Rose-style ban reason (whatever the admin typed after /ban <user>), shown
  // beside the "You have been banned" notice when present.
  const [banReason, setBanReason] = useState<string | null>(null);
  // Topic-list entrance aura: a decorative halftone color wash chosen once per
  // visit (rotates on consecutive visits) that replays whenever the user
  // returns to the list. `auraGrad` = palette 0..9; `auraKey` remounts it.
  const [auraGrad, setAuraGrad] = useState(0);
  const [auraKey, setAuraKey] = useState(0);
  const [listMenuOpen, setListMenuOpen] = useState(false);
  // Telegram Mini-App fullscreen toggle for the topic-list ⋮ menu.
  const { canFullscreen, isFullscreen, toggleFullscreen } =
    useTelegramFullscreen();
  // Topic-list search: null = closed, string = open with that query.
  const [listSearch, setListSearch] = useState<string | null>(null);
  const [showNotif, setShowNotif] = useState(false);
  // "Customize background" from the topic-list ⋮ menu — same per-device
  // localStorage preference the in-chat menu edits (picker is client-only,
  // so it's available to everyone, signed-in or not).
  const [showBgPicker, setShowBgPicker] = useState(false);
  // Live topic-list meta: the page is server-rendered ONCE, then the Mini
  // App lives for minutes/hours — without a refresher the "N members" label
  // and the Group Chat preview stay frozen at load time (owner saw "1
  // member" while 2 were in). Polls the session-free ?meta=1 endpoint
  // (5s-memoized server side) and re-ticks on visibility + topic close.
  const [liveMeta, setLiveMeta] = useState<{
    memberLabel: string;
    chatPreview: ChatPreview | null;
    /** Newest live post per topic (all topics, not just the group chat). */
    lastByTopic: Record<string, TopicPreview>;
  } | null>(null);
  // Vouch history is initially passed as server props, but bot imports can
  // arrive while the Mini App stays open. Refresh only the active read-only
  // topic so the list remains cheap and the open feed catches new posts.
  const [liveVouches, setLiveVouches] = useState(() => ({
    testimonials,
    buy4u,
    announcements,
  }));
  useEffect(() => {
    if (
      active !== "testimonials" &&
      active !== "buy4u" &&
      active !== "announcements"
    ) {
      return;
    }
    const section = active;
    let stopped = false;
    const refresh = async () => {
      if (document.hidden) return;
      try {
        const res = await fetch(
          `/api/community/vouches?section=${encodeURIComponent(section)}`,
          { cache: "no-store" },
        );
        if (!res.ok || stopped) return;
        const data = (await res.json()) as {
          ok?: boolean;
          vouches?: VouchView[];
        };
        if (!stopped && data.ok && Array.isArray(data.vouches)) {
          setLiveVouches((prev) => ({ ...prev, [section]: data.vouches! }));
        }
      } catch {
        /* transient — keep the last known history */
      }
    };
    void refresh();
    const timer = window.setInterval(refresh, 30_000);
    const onVisibility = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active]);
  // Topic-list unread badges (Web A's blue ChatBadge): newest live/vouch ids
  // per topic from the same ?meta=1 poll, counted client-side against the
  // per-device rg_seen_* / rg_vseen_* watermarks. seenTick re-reads the
  // watermarks whenever the user lands back on the list (rg_seen_* advances
  // inside CommunityChat while a topic is open).
  const [unreadSnap, setUnreadSnap] = useState<{
    live?: Record<string, string[]>;
    vouch?: Record<string, string[]>;
  } | null>(null);
  const [seenTick, setSeenTick] = useState(0);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const res = await fetch("/api/community/chat?meta=1");
        if (!res.ok || stop) return;
        const data = (await res.json()) as {
          memberCount: number | null;
          lastMessage: ChatPreview | null;
          unread?: {
            live?: Record<string, string[]>;
            vouch?: Record<string, string[]>;
          } | null;
          lastByTopic?: Record<string, TopicPreview> | null;
        };
        if (stop) return;
        if (data.unread) setUnreadSnap(data.unread);
        setLiveMeta({
          memberLabel:
            typeof data.memberCount === "number" && data.memberCount > 0
              ? `${data.memberCount} member${
                  data.memberCount === 1 ? "" : "s"
                }`
              : "public group",
          chatPreview: data.lastMessage,
          lastByTopic: data.lastByTopic ?? {},
        });
      } catch {
        /* transient — keep last known values */
      }
    };
    void tick();
    const timer = window.setInterval(tick, 15_000);
    const onVis = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [active]);
  const effMemberLabel = liveMeta?.memberLabel ?? memberLabel;
  const effChatPreview = liveMeta ? liveMeta.chatPreview : chatPreview;
  // Newest live post per topic: the poll's snapshot once it lands, the
  // server-seeded map until then.
  const effTopicPreviews = liveMeta ? liveMeta.lastByTopic : topicPreviews;
  // Unsent composer drafts per topic (written by CommunityChat under the
  // same tg_draft:* keys) — re-read every time the user lands back on the
  // topic list so the red "Draft:" preview is always current.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    if (active !== null) return;
    // Back on the list: re-read the seen watermarks too — rg_seen_* advanced
    // inside CommunityChat while the topic was open, so its badge clears now.
    setSeenTick((v) => v + 1);
    const next: Record<string, string> = {};
    try {
      for (const t of TOPICS) {
        const v = localStorage.getItem(`tg_draft:${t.key}`);
        if (v && v.trim()) next[t.key] = v;
      }
    } catch {
      return;
    }
    setDrafts(next);
  }, [active]);
  // First-visit baseline: a device with no stored watermark treats existing
  // history as read (exactly like joining a Telegram group) — badges only
  // count what arrives AFTER this. Never touches an existing rg_seen_* value
  // (that watermark belongs to CommunityChat's unread-divider logic).
  useEffect(() => {
    if (!unreadSnap) return;
    try {
      for (const t of TOPICS) {
        // A topic with NO history yet still gets a baseline ("0" = nothing
        // seen). Without it the watermark stays unwritten until that topic's
        // FIRST post arrives, which then becomes its own baseline — so the
        // first message ever posted to a quiet topic never badged its row.
        const maxLive = unreadSnap.live?.[t.key]?.[0];
        if (localStorage.getItem(`rg_seen_${t.key}`) === null)
          localStorage.setItem(`rg_seen_${t.key}`, maxLive ?? "0");
        const maxVouch = unreadSnap.vouch?.[t.key]?.[0];
        if (localStorage.getItem(`rg_vseen_${t.key}`) === null)
          localStorage.setItem(`rg_vseen_${t.key}`, maxVouch ?? "0");
      }
    } catch {
      /* storage unavailable */
    }
    setSeenTick((v) => v + 1);
  }, [unreadSnap]);
  // Opening a topic marks its vouch HISTORY seen (imported/readonly rows
  // don't live-append while the topic is open, so open == read for them).
  // The live watermark stays CommunityChat's job — it only advances when the
  // viewer actually reaches the bottom, so a badge survives a partial visit
  // exactly like the real client.
  // Marked once per open (ref) — retries when the first snapshot lands after
  // a fast open, but never keeps advancing for vouches that arrive mid-visit.
  const vouchMarkedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active) {
      vouchMarkedRef.current = null;
      return;
    }
    if (vouchMarkedRef.current === active) return;
    const maxVouch = unreadSnap?.vouch?.[active]?.[0];
    if (!maxVouch) return; // snapshot not here yet — effect re-runs on arrival
    vouchMarkedRef.current = active;
    try {
      const cur = Number(localStorage.getItem(`rg_vseen_${active}`) ?? "0");
      if (!Number.isFinite(cur) || Number(maxVouch) > cur)
        localStorage.setItem(`rg_vseen_${active}`, maxVouch);
    } catch {
      /* storage unavailable */
    }
  }, [active, unreadSnap]);
  // Per-row unread counts. A missing watermark counts as 0 (the baseline
  // effect above fills it an instant later), so a brand-new device never
  // flashes the whole history as unread. Ids arrive NEWEST-first — stop at
  // the first already-seen id.
  const unreadCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    if (!unreadSnap) return counts;
    const tally = (ids: string[] | undefined, seenRaw: string | null) => {
      if (!ids || ids.length === 0) return 0;
      // A MISSING watermark means the baseline effect hasn't run yet — count
      // nothing, so a brand-new device never flashes its whole history as
      // unread. A STORED "0" is a real watermark ("this topic was empty when
      // I arrived"), so everything posted since is genuinely unread.
      if (seenRaw === null) return 0;
      const seen = Number(seenRaw);
      if (!Number.isFinite(seen) || seen < 0) return 0;
      let n = 0;
      for (const id of ids) {
        if (Number(id) > seen) n += 1;
        else break;
      }
      return n;
    };
    try {
      for (const t of TOPICS) {
        if (t.key === active) continue; // the open topic never badges its row
        const n =
          tally(
            unreadSnap.live?.[t.key],
            localStorage.getItem(`rg_seen_${t.key}`),
          ) +
          tally(
            unreadSnap.vouch?.[t.key],
            localStorage.getItem(`rg_vseen_${t.key}`),
          );
        if (n > 0) counts[t.key] = n;
      }
    } catch {
      /* storage unavailable */
    }
    return counts;
    // seenTick forces a localStorage re-read after each topic visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadSnap, seenTick, active]);
  // Desktop-only (≥768px): collapse the persistent topic-list pane. Read the
  // saved preference in an effect so the SSR markup matches the first paint.
  const [listCollapsed, setListCollapsed] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem("tg_list_collapsed") === "1")
        setListCollapsed(true);
    } catch {
      /* storage unavailable */
    }
  }, []);
  const toggleListCollapsed = () =>
    setListCollapsed((v) => {
      try {
        localStorage.setItem("tg_list_collapsed", v ? "0" : "1");
      } catch {
        /* storage unavailable */
      }
      return !v;
    });
  const listQuery = (listSearch ?? "").trim().toLowerCase();
  const visibleTopics = listQuery
    ? TOPICS.filter((t) => t.title.toLowerCase().includes(listQuery))
    : TOPICS;
  const htmlRef = useRef<HTMLDivElement>(null);

  // Web A sizes everything against --vh (set from window.innerHeight) so the
  // layout tracks mobile browser chrome exactly like the real client.
  // COMPACT Mini App mode: the webview can be TALLER than the visible sheet,
  // so window.innerHeight measures past the fold and the bottom-anchored
  // footer (composer / "Topic locked" pill) rendered clipped. Telegram
  // exposes the VISIBLE height as viewportStableHeight — prefer the smaller
  // of the two, and re-sync on the bridge's viewportChanged event (sheet
  // drag / expand), which fires without a window resize.
  useEffect(() => {
    const el = htmlRef.current;
    if (!el) return;
    const set = () => {
      const wa = window.Telegram?.WebApp;
      const tg =
        typeof wa?.viewportStableHeight === "number"
          ? wa.viewportStableHeight
          : 0;
      const h = tg > 0 ? Math.min(tg, window.innerHeight) : window.innerHeight;
      el.style.setProperty("--vh", `${h * 0.01}px`);
    };
    set();
    window.addEventListener("resize", set);
    const wa = window.Telegram?.WebApp;
    wa?.onEvent?.("viewportChanged", set);
    return () => {
      window.removeEventListener("resize", set);
      wa?.offEvent?.("viewportChanged", set);
    };
  }, []);

  // Pre-paint: if we're inside a Telegram Mini App, hide EVERYTHING until the
  // ban check below resolves, so a banned member never even glimpses the topic
  // list. Runs synchronously before the first paint (layout effect) and only
  // when a Telegram launch is detected — web visitors are never gated, and the
  // initial client render matches SSR ("ok") so there is no hydration mismatch.
  useLayoutEffect(() => {
    try {
      // The Telegram bridge (window.Telegram) is injected ASYNCHRONOUSLY by
      // loadTelegramBridge(), so on a fresh launch it isn't there yet at
      // pre-paint. Telegram, however, puts the launch params in the URL up
      // front (#tgWebAppData=… / ?tgWebApp…) — that fragment is present before
      // any script runs, so it's the reliable synchronous "inside Telegram"
      // signal. Fall back to the bridge for later client-side remounts.
      const launched =
        window.location.hash.includes("tgWebApp") ||
        window.location.search.includes("tgWebApp");
      if (launched || window.Telegram?.WebApp?.initData) setGate("checking");
    } catch {
      /* no Telegram bridge — leave "ok" */
    }
  }, []);

  // Inside the Telegram Mini App webview, the client shows an opaque loading
  // placeholder (black screen) until WebApp.ready() fires — ensureTelegramReady()
  // signals that at SHELL mount (idempotent). We also sign in here with device
  // signals so a banned member (or a device-evading ban) is BLOCKED from the
  // whole app, not just from posting. Open on the TOPIC LIST (active = null).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const inside = await ensureTelegramReady();
      if (cancelled) return;
      if (inside) {
        setInTg(true);
        // Backstop the pre-paint gate: keep everything hidden while we
        // fingerprint + auth, in case the launch fragment was already consumed
        // (e.g. a client-side remount) and the layout effect missed it.
        setGate("checking");
      }
      // Launch deep link: a `?startapp=m_<topic>_<id>` (from Copy Link /
      // Forward) opens the Mini App straight into that topic; the message id is
      // handed to CommunityChat via the `#msg-<id>` hash it already scrolls to.
      const target = parseStartParam(readStartParam());
      if (target) {
        setActive(target.topic);
        if (target.messageId) {
          window.history.replaceState(null, "", `#msg-${target.messageId}`);
        }
      }
      const initData = window.Telegram?.WebApp?.initData;
      if (!inside || !initData) {
        if (!cancelled) setGate("ok");
        return;
      }
      try {
        const signals = await getDeviceSignals().catch(() => ({
          fp: null,
          did: null,
          signals: {} as Record<string, string>,
        }));
        const res = await fetch("/api/community/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            initData,
            fp: signals.fp,
            did: signals.did,
            signals: signals.signals,
          }),
        });
        const data = (await res.json().catch(() => null)) as {
          ok?: boolean;
          banned?: boolean;
          banReason?: string | null;
        } | null;
        if (!cancelled) {
          setBanReason(data?.banned ? (data?.banReason ?? null) : null);
          setGate(data?.banned ? "blocked" : "ok");
        }
      } catch {
        // Fail OPEN: a network hiccup must not lock out a legitimate member.
        if (!cancelled) setGate("ok");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pick this visit's aura palette once, rotating from the last visit so
  // consecutive opens never repeat, then remount the aura whenever we return
  // to the topic list (active === null) to replay the breathe-then-fade.
  useEffect(() => {
    let idx = Math.floor(Math.random() * AURA_COUNT);
    try {
      const raw = window.localStorage.getItem("tg_grad_last");
      const last = raw === null ? NaN : Number(raw);
      if (Number.isFinite(last) && last >= 0 && last < AURA_COUNT) {
        idx = (last + 1 + Math.floor(Math.random() * (AURA_COUNT - 1))) % AURA_COUNT;
      }
      window.localStorage.setItem("tg_grad_last", String(idx));
    } catch {
      /* private mode — fall back to the random pick */
    }
    setAuraGrad(idx);
  }, []);
  useEffect(() => {
    if (active === null) setAuraKey((k) => k + 1);
  }, [active]);

  // `/community#<topic>` deep link (e.g. a buttonurl or the READ ME rules
  // link pointing at /community#readme): open that topic on mount and on any
  // later hash change. `#msg-<id>` hashes are CommunityChat scroll targets,
  // not topic keys — the key check ignores them.
  useEffect(() => {
    const applyHash = () => {
      const h = window.location.hash.replace(/^#/, "").toLowerCase();
      const hit = TOPICS.find((t) => t.key === h);
      if (hit) setActive(hit.key);
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  // Admin edits + pins to read-only posts are applied client-side over the
  // server-fetched vouches so the bubble updates instantly without a refetch
  // (which would flicker the whole topic). Keyed by vouch id.
  const [vouchEdits, setVouchEdits] = useState<Record<string, string>>({});
  const [vouchPins, setVouchPins] = useState<Record<string, boolean>>({});
  // Constant "seed" bubbles (READ ME, the welcome card, the announcement seed)
  // are admin-editable too. Their edit persists server-side (welcome →
  // mod_config, readme/announcement → content_blocks) but the bodies arrive as
  // SERVER props, so after a save we patch a client override keyed by the
  // seed's menu id. Effective body = client edit ?? server override ?? built-in.
  const [seedEdits, setSeedEdits] = useState<Record<string, string>>({});
  const onSeedEdited = (id: string, body: string) =>
    setSeedEdits((prev) => ({ ...prev, [id]: body }));
  // Seed pin/delete state works the same way: the server persists it in
  // content_blocks (community_seed_pin/_hidden:<slot>), the page passes it as
  // props, and a client override keyed by menu id patches the view instantly.
  const [seedPinEdits, setSeedPinEdits] = useState<Record<string, boolean>>({});
  const [seedHiddenEdits, setSeedHiddenEdits] = useState<
    Record<string, boolean>
  >({});
  // Admin hard-deletes of vouch rows, applied client-side (no refetch).
  const [vouchDeletes, setVouchDeletes] = useState<Set<string>>(new Set());
  const seedPin = (slot: string) =>
    seedPinEdits[`seed:${slot}`] ?? seedPins[slot] ?? false;
  const seedGone = (slot: string) =>
    seedHiddenEdits[`seed:${slot}`] ?? seedHidden[slot] ?? false;
  // Pin + delete callbacks shared by vouch rows AND seed bubbles — routed by
  // id shape ("seed:<slot>" vs numeric vouch id).
  const onAnyPinned = (id: string, pinned: boolean) => {
    if (id.startsWith("seed:")) {
      setSeedPinEdits((prev) => ({ ...prev, [id]: pinned }));
    } else {
      setVouchPins((prev) => ({ ...prev, [id]: pinned }));
    }
  };
  const onAnyDeleted = (id: string) => {
    if (id.startsWith("seed:")) {
      setSeedHiddenEdits((prev) => ({ ...prev, [id]: true }));
    } else {
      setVouchDeletes((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
  };
  const effWelcome = seedEdits["seed:welcome"] ?? welcome;
  const effReadme =
    seedEdits["seed:readme"] ?? (seedReadme || README_SEED_TEXT);
  const readmeOverridden =
    seedEdits["seed:readme"] !== undefined || seedReadme.length > 0;
  const effAnnouncement =
    seedEdits["seed:announcement"] ?? (seedAnnouncement || ANNOUNCEMENT_SEED_TEXT);
  const announcementOverridden =
    seedEdits["seed:announcement"] !== undefined || seedAnnouncement.length > 0;
  const effChatNotice =
    seedEdits["seed:chat-notice"] ?? (seedChatNotice || CHAT_NOTICE_SEED_TEXT);
  const chatNoticeOverridden =
    seedEdits["seed:chat-notice"] !== undefined || seedChatNotice.length > 0;
  const applyEdits = (list: VouchView[]): VouchView[] =>
    list.filter((v) => !vouchDeletes.has(v.id)).map((v) => {
      const body = vouchEdits[v.id] !== undefined ? vouchEdits[v.id] : v.body;
      const pinned =
        vouchPins[v.id] !== undefined ? vouchPins[v.id] : v.pinned;
      return body === v.body && pinned === v.pinned
        ? v
        : { ...v, body, pinned };
    });
  const byTopic: Record<string, VouchView[]> = {
    testimonials: applyEdits(liveVouches.testimonials),
    buy4u: applyEdits(liveVouches.buy4u),
    announcements: applyEdits(liveVouches.announcements),
  };

  const rowMetaBase = (key: TopicKey): RowMeta => {
    if (key === "readme") {
      // The readme topic's one (and last) message is the readme seed bubble —
      // preview THAT (with any admin edit applied), not the welcome blurb,
      // and give the row a time like every other topic (announcements does
      // the same with its seed time).
      // The readme's decorative title is a run of custom "letter-tile" emoji
      // spelling READ ME; their fallback glyph is 🔠 (a blue "ABCD" box), which
      // looks broken repeated seven times in a plain-text preview. Collapse the
      // leading run to the literal words those tiles spell.
      const text = tokenPreview(effReadme)
        .replace(/\s+/g, " ")
        .replace(/^(?:\u{1F520})+/u, "READ ME")
        .trim();
      return {
        sender: SEED_AUTHOR,
        summary:
          text ||
          "READ ME❗️ If you haven't already, we kindly ask you to please visit our website…",
        time: README_SEED_TIME,
      };
    }
    if (key === "chat") {
      if (!effChatPreview) {
        return { sender: null, summary: "Talk to the community", time: "" };
      }
      const text = tokenPreview(effChatPreview.body)
        .replace(/\s+/g, " ")
        .trim();
      return {
        sender: effChatPreview.authorName,
        summary: text || "Photo",
        time: shortDateLabel(effChatPreview.createdAt),
      };
    }
    // Read-only topics interleave imported vouches with live posts (see the
    // chronological message list). The row must preview whichever source is
    // actually newest, or the list advertises an older post than the one the
    // topic opens on. Parsed to epoch ms — the two sources are separate code
    // paths and need not agree on timestamp formatting.
    const lastVouch = latest(byTopic[key] ?? []);
    const livePost = effTopicPreviews[key] ?? null;
    const stamp = (iso: string | null | undefined) => {
      const ms = iso ? Date.parse(iso) : NaN;
      return Number.isFinite(ms) ? ms : 0;
    };
    if (
      livePost &&
      (!lastVouch ||
        stamp(livePost.createdAt) >
          stamp(lastVouch.originDate ?? lastVouch.createdAt))
    ) {
      const liveText = tokenPreview(livePost.body).replace(/\s+/g, " ").trim();
      return {
        sender: livePost.authorName,
        summary: liveText || (livePost.hasMedia ? "Photo" : ""),
        time: shortDateLabel(livePost.createdAt),
      };
    }
    const last = lastVouch;
    if (!last) {
      // Announcements' latest post in the real group is the seeded "ask the
      // bot" notice, so mirror it in the row preview until the bot ingests more.
      if (key === "announcements") {
        return {
          sender: SEED_AUTHOR,
          summary:
            "Dear members, Do you have a question, and do not want to wait for a response?",
          time: ANNOUNCEMENT_SEED_TIME,
        };
      }
      return { sender: null, summary: "No messages yet", time: "" };
    }
    const text = tokenPreview(last.body).replace(/\s+/g, " ").trim();
    return {
      sender: last.authorName,
      summary: text || (last.mediaIds.length > 0 ? "Photo" : ""),
      time: shortDateLabel(last.originDate ?? last.createdAt),
    };
  };
  // Telegram-style draft preview: an unsent composer draft REPLACES the
  // row's sender+summary (red "Draft:" prefix) but keeps the last-message
  // timestamp, exactly like the real client.
  const rowMeta = (key: TopicKey): RowMeta => {
    const base = rowMetaBase(key);
    const draft = drafts[key];
    if (!draft) return base;
    return {
      ...base,
      sender: null,
      summary: tokenPreview(draft).replace(/\s+/g, " ").trim(),
      draft: true,
    };
  };

  // Inside the Mini App, links out of the replica must NOT navigate the
  // webview (that strands the user on the main site inside Telegram, with no
  // way back to the mini app). Route t.me links through the Telegram client
  // and everything else to the EXTERNAL browser via the bridge.
  useEffect(() => {
    if (!inTg) return;
    const root = htmlRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]");
      if (!anchor || !root.contains(anchor)) return;
      const href = anchor.getAttribute("href") ?? "";
      // In-app topic rows use `#key` hashes — leave those alone.
      if (!href || href.startsWith("#")) return;
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.protocol !== "http:" && url.protocol !== "https:") return;
      // Links within the replica itself stay in the webview.
      if (
        url.origin === window.location.origin &&
        url.pathname.startsWith("/community")
      ) {
        return;
      }
      e.preventDefault();
      const wa = window.Telegram?.WebApp;
      const abs = url.toString();
      const isTgLink =
        url.hostname === "t.me" || url.hostname === "telegram.me";
      try {
        if (isTgLink && wa?.openTelegramLink) {
          wa.openTelegramLink(abs);
        } else if (wa?.openLink) {
          wa.openLink(abs);
        } else {
          window.open(abs, "_blank", "noopener");
        }
      } catch {
        window.open(abs, "_blank", "noopener");
      }
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [inTg]);

  // Swipe-back refs — declared HERE (not next to their handlers below) because
  // they are hooks and the ban-gate early return follows. Rendering them after
  // the return crashed the Mini App with React #300 ("Rendered fewer hooks
  // than expected") the moment the gate flipped ok → checking after hydration.
  const middleRef = useRef<HTMLDivElement | null>(null);
  const backSwipe = useRef<{
    x: number;
    y: number;
    dragging: boolean;
    dead: boolean;
  } | null>(null);

  // Ban gate: while checking this stays a blank themed screen; once BLOCKED it
  // shows a Rose-style "You have been banned" notice with the reason the admin
  // typed after /ban right beside it (when one was given). No topic list, no
  // chat. ALL hooks must live above this line — see the swipe-back refs note.
  if (gate !== "ok") {
    return (
      <div className="tg-app">
        <div
          ref={htmlRef}
          className="tg-html theme-light"
          data-message-text-size="15"
        >
          <div className="tg-body Y7owXZmb is-pointer-env">
            <div
              className="tg-access-block"
              aria-hidden={gate !== "blocked"}
            >
              {gate === "blocked" && (
                <div className="tg-banned-note" role="alert">
                  <div className="tg-banned-icon" aria-hidden>
                    🚫
                  </div>
                  <div className="tg-banned-title">You have been banned</div>
                  {banReason ? (
                    <div className="tg-banned-reason">
                      <span className="tg-banned-reason-label">Reason:</span>{" "}
                      {banReason}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const back = () => {
    setActive(null);
    // Clear a stale `#<topic>` hash on close (replaceState fires no
    // hashchange, so no loop) — otherwise re-clicking a deep-link button
    // (e.g. the welcome bubble's /community#readme) is a no-op because the
    // hash never changes. ONLY topic-key hashes are cleared: Mini-App
    // `#tgWebApp...` launch fragments must never be touched.
    const h = window.location.hash.replace(/^#/, "").toLowerCase();
    if (TOPICS.some((t) => t.key === h)) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  };

  // Web A-style tap ripple on topic rows: the vendored stylesheet already
  // ships .ripple-container/.ripple-wave and the ripple-animation keyframes —
  // this just spawns the wave <span> at the press point, exactly what Web A's
  // RippleEffect component does. Self-cleaning after the 0.7s animation.
  const spawnRipple = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    const btn = e.currentTarget;
    const container = btn.querySelector(".ripple-container");
    if (!container) return;
    const rect = btn.getBoundingClientRect();
    const size = rect.width / 2;
    const wave = document.createElement("span");
    wave.className = "ripple-wave";
    wave.style.width = `${size}px`;
    wave.style.height = `${size}px`;
    wave.style.left = `${e.clientX - rect.left - size / 2}px`;
    wave.style.top = `${e.clientY - rect.top - size / 2}px`;
    container.appendChild(wave);
    window.setTimeout(() => wave.remove(), 700);
  };

  // ── Mobile swipe-back (≤767px) ─────────────────────────────────────
  // iOS-style edge gesture: a touch starting within 28px of the left screen
  // edge drags the whole chat column with the finger; releasing past 90px
  // slides it away and returns to the topic list. The drag writes an inline
  // transform on #MiddleColumn directly so no re-renders happen per move.
  // MessageBubble ignores edge-started touches for its swipe-to-reply.
  // (middleRef/backSwipe are declared above the ban-gate early return.)
  const onMiddleTouchStart = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (active === null || e.touches.length > 1) return;
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    const t = e.touches[0];
    if (!t || t.clientX >= 28) return;
    backSwipe.current = {
      x: t.clientX,
      y: t.clientY,
      dragging: false,
      dead: false,
    };
  };
  const onMiddleTouchMove = (e: ReactTouchEvent<HTMLDivElement>) => {
    const s = backSwipe.current;
    const el = middleRef.current;
    if (!s || s.dead || !el) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (!s.dragging) {
      if (Math.abs(dy) > Math.abs(dx)) {
        // vertical intent — let the message list scroll
        if (Math.abs(dy) > 12) s.dead = true;
        return;
      }
      if (dx < 8) return;
      s.dragging = true;
      el.style.transition = "none";
    }
    el.style.transform = `translateX(${Math.max(0, dx)}px)`;
  };
  const onMiddleTouchEnd = (e: ReactTouchEvent<HTMLDivElement>) => {
    const s = backSwipe.current;
    backSwipe.current = null;
    const el = middleRef.current;
    if (!s || !s.dragging || !el) return;
    const t = e.changedTouches[0];
    const dx = t ? t.clientX - s.x : 0;
    el.style.transition = "transform 0.2s ease-out";
    if (dx > 90) {
      // Commit: finish the slide offscreen, then show the list and hand
      // the column's transform back to the stylesheet.
      el.style.transform = "translateX(100vw)";
      window.setTimeout(() => {
        back();
        el.style.transition = "";
        el.style.transform = "";
      }, 210);
    } else {
      // Revert: spring back into place.
      el.style.transform = "translateX(0px)";
      window.setTimeout(() => {
        el.style.transition = "";
        el.style.transform = "";
      }, 210);
    }
  };

  // In the Mini App the ✕ must close the sheet (Web A parity); on the web it
  // returns to the main site. router.push inside Telegram stranded users on
  // the site within the webview.
  const closeApp = () => {
    if (inTg) {
      const wa = window.Telegram?.WebApp;
      if (wa?.close) {
        try {
          wa.close();
          return;
        } catch {
          /* old client — fall through */
        }
      }
      return; // never navigate the webview to the main site
    }
    router.push("/");
  };

  const mainCls = [
    "Transition_slide",
    "Transition_slide-active",
    "opacity-transition",
    "fast",
    active === null ? "left-column-shown left-column-open" : "",
    listCollapsed ? "tg-list-collapsed" : "",
    "right-column-not-shown",
    "right-column-not-open",
  ]
    .filter(Boolean)
    .join(" ");

  let middle: ReactNode = null;
  if (active === null) {
    middle = (
      <div className="tg-empty-middle">
        <span className="tg-action">Select a topic to start messaging</span>
      </div>
    );
  } else if (active === "readme") {
    // READ ME is now an admin-postable feed: CommunityChat supplies the header,
    // the live message list and (for admins) the composer, while the original
    // welcome post + welcome banner are injected as its read-only seed. Members
    // see the "Topic locked" footer instead of an input (gated in CommunityChat
    // and enforced server-side in the chat POST route).
    const def = TOPICS.find((t) => t.key === "readme");
    middle = (
      <CommunityChat
        key="readme"
        onBack={back}
        topic="readme"
        title={def?.title ?? "READ ME"}
        icon={def ? <TopicIcon def={def} /> : undefined}
        onSeedEdited={onSeedEdited}
        onVouchPinned={onAnyPinned}
        onVouchDeleted={onAnyDeleted}
        pinnedExtras={[
          ...(seedPin("readme") && !seedGone("readme")
            ? [{ id: "seed:readme", body: effReadme }]
            : []),
          ...(seedPin("welcome") && !seedGone("welcome") && effWelcome
            ? [{ id: "seed:welcome", body: effWelcome }]
            : []),
        ]}
        history={(
          query,
          onReadonlyMenu,
          onOpenMedia,
          pinnedOnly,
          reactionsFor,
          onReact,
        ) => {
          const rq = query.trim().toLowerCase();
          const showSeed =
            !seedGone("readme") &&
            (!rq || effReadme.toLowerCase().includes(rq)) &&
            (!pinnedOnly || seedPin("readme"));
          const showWelcome =
            !!effWelcome &&
            !seedGone("welcome") &&
            (!rq || effWelcome.toLowerCase().includes(rq)) &&
            (!pinnedOnly || seedPin("welcome"));
          if (!showSeed && !showWelcome) return null;
          return (
            <div className="message-date-group first-message-date-group">
              <div className="sender-group-container sKXqbu2I">
                {showSeed && (
                  <MessageBubble
                    own
                    first
                    last={!showWelcome}
                    hasAppendix={!showWelcome}
                    pinned={seedPin("readme")}
                    mid="seed:readme"
                    media={[README_SEED_PHOTO]}
                    mediaSize={{ w: 639, h: 450 }}
                    reactions={reactionsFor(
                      "seed:readme",
                      README_SEED_REACTIONS,
                    )}
                    onReact={(e) => onReact("seed:readme", e)}
                    body={readmeOverridden ? renderBody(effReadme) : README_SEED_BODY}
                    time={README_SEED_TIME}
                    onOpenMenu={(pos) =>
                      onReadonlyMenu(pos, {
                        id: "seed:readme",
                        text: effReadme,
                        pinned: seedPin("readme"),
                        canModify: true,
                        canPin: true,
                        canDelete: true,
                      })
                    }
                    onOpenMedia={onOpenMedia}
                  />
                )}
                {showWelcome && (
                  <MessageBubble
                    own
                    first={!showSeed}
                    last
                    hasAppendix
                    pinned={seedPin("welcome")}
                    mid="seed:welcome"
                    body={renderBody(effWelcome)}
                    reactions={reactionsFor("seed:welcome")}
                    onReact={(e) => onReact("seed:welcome", e)}
                    onOpenMenu={(pos) =>
                      onReadonlyMenu(pos, {
                        id: "seed:welcome",
                        text: effWelcome,
                        pinned: seedPin("welcome"),
                        canModify: true,
                        canPin: true,
                        canDelete: true,
                      })
                    }
                  />
                )}
              </div>
            </div>
          );
        }}
      />
    );
  } else if (
    active === "announcements" ||
    active === "buy4u" ||
    active === "testimonials"
  ) {
    const def = TOPICS.find((t) => t.key === active);
    const topicKey = active;
    middle = (
      <CommunityChat
        key={active}
        onBack={back}
        topic={active}
        title={def?.title ?? ""}
        icon={def ? <TopicIcon def={def} /> : undefined}
        chronologicalHistory
        onVouchEdited={(id, body) =>
          setVouchEdits((prev) => ({ ...prev, [id]: body }))
        }
        onVouchPinned={onAnyPinned}
        onVouchDeleted={onAnyDeleted}
        onSeedEdited={onSeedEdited}
        chatNoticeText={effChatNotice}
        chatNoticeOverridden={chatNoticeOverridden}
        chatNoticePinned={seedPin("chat-notice")}
        chatNoticeHidden={seedGone("chat-notice")}
        pinnedExtras={[
          ...(topicKey === "announcements" &&
          seedPin("announcement") &&
          !seedGone("announcement")
            ? [{ id: "seed:announcement", body: effAnnouncement }]
            : []),
          ...(topicKey === "testimonials" &&
          seedPin("chat-notice") &&
          !seedGone("chat-notice")
            ? [{ id: "seed:chat-notice", body: effChatNotice }]
            : []),
          ...(byTopic[topicKey] ?? [])
            .filter((v) => v.pinned)
            .sort((a, b) => {
              const ta = a.originDate ?? a.createdAt;
              const tb = b.originDate ?? b.createdAt;
              if (ta !== tb) return ta < tb ? -1 : 1;
              return Number(a.id) - Number(b.id);
            })
            .map((v) => ({ id: `v${v.id}`, body: v.body ?? "" })),
        ]}
        history={(
          query,
          onReadonlyMenu,
          onOpenMedia,
          pinnedOnly,
          reactionsFor,
          onReact,
          range,
        ) => {
          const q = query.trim().toLowerCase();
          const vouches = byTopic[topicKey] ?? [];
          const shown = (
            q
              ? vouches.filter(
                  (v) =>
                    v.body.toLowerCase().includes(q) ||
                    v.authorName.toLowerCase().includes(q),
                )
              : vouches
          ).filter((v) => {
            if (pinnedOnly && !v.pinned) return false;
            const stamp = v.originDate ?? v.createdAt;
            if (range?.through && stamp > range.through) return false;
            if (range?.after && stamp <= range.after) return false;
            return true;
          });
          return (
            <>
              {topicKey === "announcements" &&
                !range?.after &&
                !q &&
                !seedGone("announcement") &&
                (!pinnedOnly || seedPin("announcement")) && (
                <div
                  className="message-date-group first-message-date-group"
                  style={{ order: TIMELINE_SEED_ORDER }}
                >
                  <div className="sender-group-container sKXqbu2I">
                    <MessageBubble
                      own
                      first
                      last
                      hasAppendix
                      pinned={seedPin("announcement")}
                      mid="seed:announcement"
                      media={[ANNOUNCEMENT_SEED_PHOTO]}
                      mediaSize={{ w: 1280, h: 549 }}
                      body={
                        announcementOverridden
                          ? renderBody(effAnnouncement)
                          : ANNOUNCEMENT_SEED_BODY
                      }
                      time={ANNOUNCEMENT_SEED_TIME}
                      reactions={reactionsFor("seed:announcement")}
                      onReact={(e) => onReact("seed:announcement", e)}
                      onOpenMenu={(pos) =>
                        onReadonlyMenu(pos, {
                          id: "seed:announcement",
                          text: effAnnouncement,
                          pinned: seedPin("announcement"),
                          canModify: true,
                          canPin: true,
                          canDelete: true,
                        })
                      }
                      onOpenMedia={onOpenMedia}
                    />
                  </div>
                </div>
              )}
              <VouchHistory
                vouches={shown}
                onOpenMenu={onReadonlyMenu}
                onOpenMedia={onOpenMedia}
                reactionsFor={reactionsFor}
                onReact={onReact}
                hideDates={topicKey === "buy4u"}
                chronological
              />
            </>
          );
        }}
      />
    );
  } else {
    middle = (
      <CommunityChat
        key="chat"
        onBack={back}
        onSeedEdited={onSeedEdited}
        onVouchPinned={onAnyPinned}
        onVouchDeleted={onAnyDeleted}
        chatNoticeText={effChatNotice}
        chatNoticeOverridden={chatNoticeOverridden}
        chatNoticePinned={seedPin("chat-notice")}
        chatNoticeHidden={seedGone("chat-notice")}
        pinnedExtras={
          seedPin("chat-notice") && !seedGone("chat-notice")
            ? [{ id: "seed:chat-notice", body: effChatNotice }]
            : []
        }
      />
    );
  }

  return (
    <div className="tg-app">
      <div
        ref={htmlRef}
        className="tg-html theme-light"
        data-message-text-size="15"
      >
        <div className="tg-body Y7owXZmb is-pointer-env">
          <div id="Main" className={mainCls} style={MAIN_STYLE}>
            <div id="LeftColumn" className="Transition">
              <div
                id="LeftColumn-main"
                className="Transition_slide Transition_slide-active"
              />
              <div className="m2EjaIDq" style={{ transform: "none" }}>
                <div id="TopicListHeader" className="left-header">
                  {listSearch === null ? (
                    <>
                      <button
                        type="button"
                        className="Button smaller translucent round"
                        aria-label="Close"
                        title="Close"
                        onClick={closeApp}
                      >
                        <i className="icon icon-close" aria-hidden />
                      </button>
                      <div className="ChatInfo RpNeGy4Y">
                        <div className="info">
                          <div className="title eZi5Ws-N">
                            <h3
                              dir="auto"
                              role="button"
                              className="fullName _8IVW0azL"
                            >
                              RefundGod
                            </h3>
                          </div>
                          <span className="status">
                            <span className="group-status">
                              {effMemberLabel}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="HeaderActions">
                        <button
                          type="button"
                          className="Button smaller translucent round tg-desk-only"
                          aria-label="Hide topic list"
                          title="Hide topic list"
                          onClick={toggleListCollapsed}
                        >
                          <i className="icon icon-sidebar" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="Button smaller translucent round"
                          aria-label="Search topics"
                          title="Search topics"
                          onClick={() => setListSearch("")}
                        >
                          <i className="icon icon-search" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="Button smaller translucent round"
                          aria-label="More actions"
                          title="More actions"
                          aria-expanded={listMenuOpen}
                          onClick={() => setListMenuOpen((v) => !v)}
                        >
                          <i className="icon icon-settings" aria-hidden />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="Button smaller translucent round"
                        aria-label="Close search"
                        title="Close search"
                        onClick={() => setListSearch(null)}
                      >
                        <i className="icon icon-arrow-left" aria-hidden />
                      </button>
                      <div className="SearchInput tg-list-search" dir="ltr">
                        <input
                          type="text"
                          dir="auto"
                          placeholder="Search"
                          className="form-control"
                          value={listSearch}
                          autoFocus
                          onChange={(e) => setListSearch(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  {listMenuOpen && (
                    <>
                      <button
                        type="button"
                        className="tg-menu-backdrop"
                        onClick={() => setListMenuOpen(false)}
                        aria-label="Close menu"
                      />
                      <div className="tg-menu" role="menu">
                        {canFullscreen && (
                          <button
                            type="button"
                            className="tg-menu-item"
                            onClick={() => {
                              setListMenuOpen(false);
                              // Remember the explicit choice so the chat's
                              // entry prompt doesn't re-ask / undo it.
                              try {
                                localStorage.setItem(
                                  FS_PREF_KEY,
                                  isFullscreen ? "skip" : "fs",
                                );
                              } catch {
                                /* private mode — non-fatal */
                              }
                              toggleFullscreen();
                            }}
                          >
                            {isFullscreen ? <IconCollapse /> : <IconExpand />}
                            {isFullscreen ? "Exit full screen" : "Full screen"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="tg-menu-item"
                          onClick={() => {
                            setListMenuOpen(false);
                            setShowNotif(true);
                          }}
                        >
                          <IconBell />
                          Notifications
                        </button>
                        <button
                          type="button"
                          className="tg-menu-item"
                          onClick={() => {
                            setListMenuOpen(false);
                            setShowBgPicker(true);
                          }}
                        >
                          <IconWallpaper />
                          Customize background
                        </button>
                        <a
                          className="tg-menu-item"
                          href={ADMIN_TG}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setListMenuOpen(false)}
                        >
                          <IconChat />
                          Message admin
                        </a>
                      </div>
                    </>
                  )}
                </div>
                <div className="biKrMjRH" />
                <div className="chat-list custom-scroll">
                  <div className="ztAcxuDG" />
                  <div
                    style={{
                      position: "relative",
                      height: visibleTopics.length * ROW_HEIGHT,
                    }}
                  >
                    {visibleTopics.map((t, i) => {
                      const meta = rowMeta(t.key);
                      const unreadN = unreadCounts[t.key] ?? 0;
                      return (
                        <div
                          key={t.key}
                          className={`ListItem L6dKWJvD Chat${
                            active === t.key ? " selected" : ""
                          } chat-item-clickable`}
                          style={{ top: i * ROW_HEIGHT }}
                        >
                          <a
                            className="ListItem-button"
                            href={`#${t.key}`}
                            tabIndex={0}
                            onMouseDown={spawnRipple}
                            onClick={(e) => {
                              e.preventDefault();
                              setActive(t.key);
                            }}
                          >
                            <div className="ripple-container" />
                            <div className="info">
                              <div className="info-row">
                                <div className="title">
                                  <TopicIcon def={t} />
                                  <h3 dir="auto" className="fullName">
                                    {t.title}
                                  </h3>
                                </div>
                                <div className="separator" />
                                <div className="LastMessageMeta">
                                  {meta.time && (
                                    <span className="time">{meta.time}</span>
                                  )}
                                </div>
                              </div>
                              <div className="subtitle">
                                <p
                                  className="last-message shared-canvas-container"
                                  dir="ltr"
                                >
                                  {meta.draft ? (
                                    <span className="tg-draft-label">
                                      Draft:
                                    </span>
                                  ) : (
                                    meta.sender && (
                                      <>
                                        <span className="sender-name">
                                          {meta.sender}
                                        </span>
                                        <span className="colon">:</span>
                                      </>
                                    )
                                  )}
                                  <span
                                    className="last-message-summary"
                                    dir="auto"
                                  >
                                    {renderTextWithEmoji(
                                      meta.summary,
                                      `pv-${t.key}`,
                                    )}
                                  </span>
                                </p>
                                {unreadN > 0 && (
                                  <div
                                    className="tg-topic-badge"
                                    aria-label={`${unreadN} unread`}
                                  >
                                    {unreadN > 99 ? "99+" : unreadN}
                                  </div>
                                )}
                              </div>
                            </div>
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div
                  key={auraKey}
                  className={`tg-topic-aura tg-topic-aura-${auraGrad}`}
                  aria-hidden
                />
              </div>
            </div>

            <div
              id="MiddleColumn"
              className="mask-image-disabled ui-ready"
              ref={middleRef}
              onTouchStart={onMiddleTouchStart}
              onTouchMove={onMiddleTouchMove}
              onTouchEnd={onMiddleTouchEnd}
              onTouchCancel={onMiddleTouchEnd}
            >
              <div className="resize-handle" />
              {/* Wallpaper layer — first positioned child so every later
                  sibling (header, portals, .messages-layout) paints above. */}
              <ChatBackground />
              <div id="middle-column-portals" />
              <button
                type="button"
                className="Button smaller translucent round tg-list-reopen"
                aria-label="Show topic list"
                title="Show topic list"
                onClick={toggleListCollapsed}
              >
                <i className="icon icon-sidebar" aria-hidden />
              </button>
              <div className="messages-layout">{middle}</div>
            </div>
          </div>
        </div>
        {showNotif && (
          <NotificationSettings onClose={() => setShowNotif(false)} />
        )}
        {/* Rendered here (outside the transformed #MiddleColumn) so the
            fixed backdrop anchors to the viewport — overlay-portal rule. */}
        {showBgPicker && (
          <ChatBackgroundPicker onClose={() => setShowBgPicker(false)} />
        )}
      </div>
    </div>
  );
}
